import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItemText,
  Collapse,
  ListItemButton,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useProductsData } from '../services/dataApi';
import { useMeta } from '../contexts/MetaContext';

const Menu = ({ onMenuClose }) => {
  const navigate = useNavigate();
  const { data: productsData } = useProductsData();
  const { metaData } = useMeta();
  
  // Get menu configuration from metadata
  const menu = metaData.menuConfig || [];
  
  // Initialize openStates with default collapsed states
  const [openStates, setOpenStates] = useState(() => {
    const initialState = {};
    const initializeStates = (items, prefix = '') => {
      items.forEach((item, index) => {
        const key = `${prefix}${item.type}-${item.title}-${index}`;
        initialState[key] = !item.collapsed; // true if not collapsed (open), false if collapsed (closed)
        if (item.items) {
          initializeStates(item.items, `${key}-`);
        }
      });
    };
    initializeStates(menu);
    return initialState;
  });

  // Menu configuration
  // const menu = [
  //   { type: 'normal', title: 'All Products', url: '/search' },
  //   { type: 'normal', title: 'Featured', url: '/search?featured=true' },
  //   { type: 'normal', title: 'Categories', collapsed: true, items: [
  //     { type: 'normal', title: 'Electronics', url: '/search?category=electronics' },
  //     { type: 'normal', title: 'Clothing', url: '/search?category=clothing' },
  //     { type: 'normal', title: 'Home & Garden', url: '/search?category=home-garden' }
  //   ]},
  //   { type: 'collections', title: 'Collections', collapsed: true },
  //   { type: 'brands', title: 'Brands', collapsed: false },
  //   { type: 'normal', title: 'Sale', url: '/search?sale=true' }
  // ];

  const collections = useMemo(() => {
    return productsData.length > 0 
      ? [...new Set(productsData.flatMap(p => p.category || []))].sort((a, b) => a.localeCompare(b))
      : [];
  }, [productsData]);
  
  const brands = useMemo(() => {
    return productsData.length > 0
      ? [...new Set(productsData.map(p => p.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b))
      : [];
  }, [productsData]);

  const handleMenuItemClick = (path) => {
    navigate(path);
    onMenuClose();
  };

  const toggleOpenState = (itemKey) => {
    setOpenStates(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const getItemKey = (item, index, parentKey = '') => {
    const key = parentKey ? `${parentKey}-${item.type}-${item.title}-${index}` : `${item.type}-${item.title}-${index}`;
    return key;
  };

  const isItemOpen = (item, index, parentKey = '') => {
    const key = getItemKey(item, index, parentKey);
    return openStates[key];
  };

  const renderMenuItem = (item, index, depth = 0, parentKey = '') => {
    const itemKey = getItemKey(item, index, parentKey);
    const isOpen = isItemOpen(item, index, parentKey);
    const hasChildren = item.items && item.items.length > 0;
    const hasDynamicChildren = item.type === 'collections' || item.type === 'brands';

    // Get dynamic children based on type
    let children = [];
    if (item.type === 'collections') {
      children = collections.map(collection => ({
        type: 'normal',
        title: collection,
        url: `/search?collection=${encodeURIComponent(collection)}`
      }));
    } else if (item.type === 'brands') {
      children = brands.map(brand => ({
        type: 'normal',
        title: brand,
        url: `/search?brand=${encodeURIComponent(brand)}`
      }));
    } else if (hasChildren) {
      children = item.items;
    }

    const shouldShowExpandIcon = hasChildren || hasDynamicChildren;

    return (
      <React.Fragment key={itemKey}>
        <ListItemButton 
          onClick={() => {
            if (item.url) {
              handleMenuItemClick(item.url);
            } else if (shouldShowExpandIcon) {
              toggleOpenState(itemKey);
            }
          }}
          sx={{ pl: depth * 2 + 2 }}
        >
          <ListItemText primary={item.title} />
          {shouldShowExpandIcon && (
            isOpen ? (
              <ExpandLess sx={{ opacity: 0.5 }} />
            ) : (
              <ExpandMore sx={{ opacity: 0.5 }} />
            )
          )}
        </ListItemButton>
        
        {(hasChildren || hasDynamicChildren) && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child, childIndex) => 
                renderMenuItem(child, childIndex, depth + 1, itemKey)
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Typography variant="h5" component="h2" sx={{ textAlign: 'center', py: 5 }}>
        Menu
      </Typography>
      <List>
        {menu.map((item, index) => renderMenuItem(item, index, 0, ''))}
      </List>
    </>
  );
};

export default Menu;
